import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  FolderKanban,
  FileText,
  PenTool,
  CheckCircle2,
  Clock,
  LogOut,
  ExternalLink,
  Mail,
  Building,
  Loader2,
  ShieldCheck
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortal() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [client, setClient] = useState(null);
  const [portalData, setPortalData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      // Check for existing session
      checkSession();
    }
  }, [token]);

  const verifyToken = async () => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API_URL}/api/client-portal/verify`, { token }, {
        withCredentials: true
      });
      
      if (res.data.success) {
        setClient(res.data.client);
        setAuthenticated(true);
        fetchPortalData();
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid or expired link");
      setLoading(false);
    } finally {
      setVerifying(false);
    }
  };

  const checkSession = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client-portal/me`, {
        withCredentials: true
      });
      
      setClient(res.data.client);
      setPortalData(res.data);
      setAuthenticated(true);
    } catch (e) {
      // No valid session
      setError("Please use the link sent to your email to access your portal.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPortalData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client-portal/me`, {
        withCredentials: true
      });
      setPortalData(res.data);
    } catch (e) {
      toast.error("Failed to load portal data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/client-portal/logout`, {}, {
        withCredentials: true
      });
      setAuthenticated(false);
      setClient(null);
      setPortalData(null);
      setError("You have been logged out. Use the link sent to your email to access your portal again.");
    } catch (e) {
      toast.error("Logout failed");
    }
  };

  if (loading || verifying) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-indigo-400">{verifying ? "Verifying your access..." : "Loading your portal..."}</p>
        </div>
      </div>
    );
  }

  if (error || !authenticated) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900/50 border-slate-700">
          <CardContent className="pt-8 pb-8 text-center">
            <ShieldCheck className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-indigo-500 mb-2">Client Portal</h1>
            <p className="text-indigo-400 mb-6">{error}</p>
            <Link
              to="/"
              className="text-indigo-400 hover:text-indigo-400 text-sm"
            >
              Return to ClientKit
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const summary = portalData?.summary || {};

  return (
    <div className="min-h-screen bg-stone-100" data-testid="client-portal">
      {/* Header */}
      <header className="bg-slate-900/50 border-b border-slate-700 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
              <Building className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-indigo-500">Client Portal</h1>
              <p className="text-sm text-indigo-400">Welcome, {client?.name || client?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-1.5 text-indigo-400 hover:text-indigo-500 hover:bg-slate-800 rounded-lg transition-colors text-sm"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <FolderKanban className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-500">{summary.total_projects || 0}</p>
                  <p className="text-xs text-indigo-400">Total Projects</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-500">{summary.active_projects || 0}</p>
                  <p className="text-xs text-indigo-400">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-500/20">
                  <Clock className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-500">{summary.pending_contracts || 0}</p>
                  <p className="text-xs text-indigo-400">Pending Signature</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-500/20">
                  <PenTool className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-500">{summary.signed_contracts || 0}</p>
                  <p className="text-xs text-indigo-400">Signed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Projects List */}
        <Card className="bg-slate-900/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-indigo-400" />
              Your Projects
            </CardTitle>
            <CardDescription className="text-indigo-400">
              View all your active and completed projects
            </CardDescription>
          </CardHeader>
          <CardContent>
            {portalData?.projects?.length > 0 ? (
              <div className="space-y-3">
                {portalData.projects.map((project) => (
                  <div
                    key={project.id}
                    className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-slate-600 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-indigo-500">{project.title}</h3>
                        <p className="text-sm text-indigo-400">
                          {project.description?.slice(0, 100) || "No description"}
                        </p>
                      </div>
                      <Badge className={`${
                        project.status === "active" ? "bg-green-500/20 text-green-400" :
                        project.status === "completed" ? "bg-blue-500/20 text-indigo-400" :
                        project.status === "paused" ? "bg-indigo-500/20 text-indigo-400" :
                        "bg-slate-500/20 text-indigo-400"
                      }`}>
                        {project.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No projects yet</p>
            )}
          </CardContent>
        </Card>

        {/* Contracts */}
        <Card className="bg-slate-900/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-400" />
              Contracts & Documents
            </CardTitle>
            <CardDescription className="text-indigo-400">
              View and sign your contracts
            </CardDescription>
          </CardHeader>
          <CardContent>
            {portalData?.contracts?.length > 0 ? (
              <div className="space-y-3">
                {portalData.contracts.map((contract) => (
                  <div
                    key={contract.id}
                    className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-indigo-400" />
                      <div>
                        <p className="font-medium text-indigo-500">{contract.title || "Contract"}</p>
                        <p className="text-xs text-slate-400">
                          Created {new Date(contract.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${
                        contract.status === "signed" ? "bg-green-500/20 text-green-400" :
                        contract.status === "pending_signature" ? "bg-indigo-500/20 text-indigo-400" :
                        "bg-slate-500/20 text-indigo-400"
                      }`}>
                        {contract.status === "signed" ? "Signed" : 
                         contract.status === "pending_signature" ? "Needs Signature" : contract.status}
                      </Badge>
                      {contract.share_links?.sign_link && contract.status !== "signed" && (
                        <a
                          href={contract.share_links.sign_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1 bg-indigo-500 hover:bg-indigo-600 text-white text-sm rounded-lg transition-colors"
                        >
                          <PenTool className="w-3 h-3" />
                          Sign
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No contracts yet</p>
            )}
          </CardContent>
        </Card>

        {/* Proposals */}
        {portalData?.proposals?.length > 0 && (
          <Card className="bg-slate-900/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-indigo-500 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-400" />
                Proposals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {portalData.proposals.map((proposal) => (
                  <div
                    key={proposal.id}
                    className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-indigo-500">{proposal.title}</p>
                        <p className="text-xs text-slate-400">
                          Version {proposal.version} • {new Date(proposal.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className={`${
                        proposal.status === "accepted" ? "bg-green-500/20 text-green-400" :
                        proposal.status === "draft" ? "bg-slate-500/20 text-indigo-400" :
                        "bg-indigo-500/20 text-indigo-400"
                      }`}>
                        {proposal.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700 mt-12 py-6">
        <p className="text-center text-slate-600 text-sm">
          Powered by <span className="text-indigo-400">ClientKit</span>
        </p>
      </footer>
    </div>
  );
}
